import moment from 'moment'
import { ApiGeneralObjectArrayPayload, CodeLabel } from '../api/types'
import { TcwFormDate } from '../domain/types'
import { TCWDataLoadError } from '@/plugins/TCWBaseError'
import { Route } from 'vue-router'
import helpPageMap from '@/assets/helpPageMap.json'

export const API_DAY_FORMAT = 'YYYY-MM-DD'
export const API_DAYTIME_FORMAT = 'YYYY-MM-DD HH:mm:SS'
const KYUJITSU_CODE_LIST = ['01', '02', '04', '07', '08', '09', '10', '11']
export const PUBLIC_HOLIDAY_CODE = '01'

export const getGetsudo = (targetDate: Date, shimebi: number): Date | null => {
  if (targetDate === null) {
    return null
  }

  let getsudo = new Date(targetDate)
  if (getsudo.getDate() > shimebi) {
    const m = moment(targetDate)
    m.add(1, 'M')
    getsudo = m.toDate()
  }
  getsudo.setDate(1)
  return getsudo
}

export const isHoliday = (kyujitsuCode: string | null): boolean => {
  return KYUJITSU_CODE_LIST.some((code: string) => code === kyujitsuCode)
}

export const isNotHoliday = (kyujitsuCode: string): boolean => {
  return !isHoliday(kyujitsuCode)
}
export const isPublicHoliday = (kyujitsuCode: string): boolean => {
  return PUBLIC_HOLIDAY_CODE === kyujitsuCode
}
export const isPrivateHoliday = (kyujitsuCode: string): boolean => {
  return kyujitsuCode==='02'
}
export const isWithoutHoliday = (kyujitsuCode: string): boolean => {
  return kyujitsuCode ==='08' || kyujitsuCode === '09'
}
export const isSpecialHoliday = (kyujitsuCode: string): boolean => {
  return kyujitsuCode ==='07' || kyujitsuCode === '10' || kyujitsuCode === '11'  
}
export const isSubstituteHoliday = (kyujitsuCode: string): boolean => {
  return kyujitsuCode ==='04' 
}
export const isHalfHoliday = (kyujitsuCode: string): boolean => {
  return kyujitsuCode === '03'
}
export const isTransferHolidayOut = (kyujitsuCode: string): boolean => {
  return kyujitsuCode ==='05' 
}
export const isHolidayWorking = (kyujitsuCode: string): boolean => {
  return kyujitsuCode ==='06' 
}
export const getShinyaJikanByDate = (
  targetDate = new Date(),
  shinyaKijunJikanStr: string
): Date => {
  // TODO
  let shinyaKijun: string[] = shinyaKijunJikanStr.split(':')
  if (shinyaKijun.length < 2) {
    shinyaKijun = ['22', '0']
  }
  let [shinyaKijunHour, shinyaKijunMinute] = [
    Number(shinyaKijun[0]),
    Number(Number(shinyaKijun[1]))
  ]
  if (Number.isNaN(shinyaKijunHour)) shinyaKijunHour = shinyaKijunHour = 22
  if (Number.isNaN(shinyaKijunMinute)) shinyaKijunMinute = shinyaKijunMinute = 0

  const baseDate = new Date(targetDate)
  baseDate.setHours(shinyaKijunHour)
  baseDate.setMinutes(shinyaKijunMinute)
  return baseDate
}

export const createKoutsuhiKeiroStr = (keiroList: ApiGeneralObjectArrayPayload): string => {
  const keiroCount = keiroList.length
  let str = ''
  keiroList.forEach((keiro, index) => {
    if (index === 0) {
      str += `${keiro.eki} →`
    } else if (0 < index && index < keiroCount - 1) {
      str += ` ${keiro.eki} →`
    } else if (index === keiroCount - 1) {
      str += ` ${keiro.eki}`
    }
  })
  return str
}

export const createGaishutsuStr = (gaishutsuList: ApiGeneralObjectArrayPayload): string => {
  let str = ''
  gaishutsuList.forEach(gaishutsu => {
    str += `・${moment(`${gaishutsu.kaishi}`).format('HH:mm')}-${moment(
      `${gaishutsu.shuryo}`
    ).format('HH:mm')} ${gaishutsu.biko}`
  })
  return str
}

/**
 * 日付文字列からDateオブジェクトを作成する。
 * Safariは日付文字列のハイフンは対応していないのでスラッシュに置き換えるS
 * @param dateStr
 * @param nullToNow
 */
export const dateOf = (dateStr: string | null | undefined, nullToNow = false): TcwFormDate => {
  if (dateStr) {
    return new Date(dateStr.replaceAll('-', '/')) // Safari用にハイフンを変換する
  } else {
    return nullToNow ? new Date() : null
  }
}

export const getActionName = (actionCode: string | number): string => {
  switch (String(actionCode)) {
    case '1':
      return '提出'
    case '2':
      return '承認'
    case '3':
      return '完了'
    case '4':
      return '取下（前承認者）'
    case '5':
      return '前回承認者へ差戻'
    case '6':
      return '取下（提出者）'
    case '7':
      return '前回承認者へ再提出'
    case '8':
      return '最初の承認者へ再提出'
    case '9':
      return '提出者へ差戻'
    case '10':
      return '完了解除'
    default:
      return ''
  }
}

export const getActionColor = (actionCode: string | number): string => {
  switch (String(actionCode)) {
    case '1':
    case '7':
    case '8':
      return 'primary'
    case '2':
    case '3':
      return 'success'
    case '4':
    case '5':
    case '6':
    case '9':
    case '10':
      return 'danger'
    default:
      return 'plain'
  }
}

export const getLabelByCode = async (
  keyClass: number | string,
  code: number | string,
  getCodeLabelList: (keyClass: number | string) => Promise<CodeLabel[]>
): Promise<string> => {
  const codeLabelList = await getCodeLabelList(keyClass)
  const codLabel = codeLabelList.find((cl: CodeLabel) => String(cl.code) === String(code))
  if (codLabel === undefined) {
    throw new TCWDataLoadError(
      `マスタデータが取得されていません。KeyClass: ${keyClass}, Code: ${code}`
    )
  }
  return codLabel.label
}

/**
 * @returns ヘルプページURL
 */
export const getApiUrlBase = (): string => {
  return process.env.VUE_APP_API_URL_BASE as string
}

export const createHash = (text: string): string => {
  let hash = 0,
    i,
    chr
  if (text.length === 0) return String(hash)
  for (i = 0; i < text.length; i++) {
    chr = text.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }
  return String(hash)
}

/**
 * 現在のルート情報にマッチしたヘルプページURLを取得する
 *
 * @param route ルート情報
 * @returns ヘルプページURL
 */
export const getHelpUrlByRoute = (route: Route): string => {
  // ヘルプページURL取得
  let jumpUrl = process.env?.VUE_APP_HELP_URL_BASE as string

  // ルート情報が設定されているかチェック
  if (route?.matched?.length === 0) {
    return jumpUrl
  }

  // 現在表示中の画面にしたがってヘルプページのタグ取得
  const matchedPath = route.matched[0].path
  const helpPageKey = Object.keys(helpPageMap).find(key => key === matchedPath)
  if (helpPageKey) {
    const tag = (helpPageMap as { [key: string]: string })[helpPageKey]
    jumpUrl += `#${tag}`
  }

  return jumpUrl
}

/**
 * 初回ログイン時のヘルプページURLを取得する
 *
 * @returns ヘルプページURL
 */
export const getFirstLoginFlowHelpUrl = (): string => {
  return process.env?.VUE_APP_HELP_URL_BASE + '#shokai_flow'
}

/**
 * 現在のルート情報にマッチしたヘルプページURLを取得する
 *
 * @param route ルート情報
 * @returns ヘルプページURL
 */
export const getHelpUrlByTag = (tag: string): string => {
  return process.env?.VUE_APP_HELP_URL_BASE + tag
}

export const getDecodedFileNameFromHeader = (headers: { [key: string]: string }): string | null => {
  if (!headers['content-disposition']) {
    return null
  }
  const contentDisposition = headers['content-disposition'] as string

  // 「filename*=[文字コード]''[ファイル名]」の部分からファイル名を抽出
  const encodedFileNamePosition = contentDisposition.indexOf("''") + 2
  if (encodedFileNamePosition > 0) {
    let fileName = contentDisposition.substring(encodedFileNamePosition, contentDisposition.length)
    fileName = decodeURI(fileName)
    return fileName
  }

  // 万が一取得できなかった場合は「filename=[ファイル名]の部分から抽出」
  const filenameSections = contentDisposition.match('filename=(.+)')
  if (filenameSections && filenameSections?.length > 1) {
    let fileName = filenameSections[1]
    fileName = decodeURI(fileName)
    return fileName
  }

  // それでも取得できない場合はnullを返す
  return null
}

export const execDownloadFile = (fileName: string, data: string): void => {
  const blob = new Blob([data])
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
